// Client mirror of the SQL is_represented() — keep in lockstep with
// 2026-08-23_retired_year_exclusive.sql (supersedes the inclusive-end version from earlier the same day).
// Years are 4-digit → string compare == numeric compare.
//
// ⛔ THE CONVENTION, stated once. ALL THREE COLUMNS NAME THE FIRST YEAR OF A NEW STATE:
//     first_year       = first year represented
//     retired_year     = first year NOT represented
//     reactivated_year = first year represented again
//   represented = [first_year, retired_year) ∪ [reactivated_year, ∞)
// Nothing here is asymmetric once read that way, and Philip's test states it exactly: retire at 65 and 65 is
// the first year you don't work. This is the THIRD time the boundary has been ambiguous — if you are about to
// change one of these three clauses, the sentence above is the thing to check the change against.
//
// ⛔ BOTH ENDS ARE OPTIONAL AND MEAN DIFFERENT THINGS:
//   first_year null  = always ours (every existing row until someone enters one)
//   retired_year null = still ours (ongoing)
// The START clause is why this exists: without it `retired == null` returned true for EVERY year ever, so a
// parcel imported with rows back to 2021 counted as represented in 2021 — 35 parcels where only 30 were ours.
export const isRepresented = (first, retired, reactivated, year) => {
  const y = String(year);
  if (first != null && y < String(first)) return false;      // before we had it
  if (retired == null) return true;                          // ongoing
  return y < String(retired) || (reactivated != null && y >= String(reactivated));
};

// Cascade: a parcel is represented in `year` only when BOTH it and its property are (mirrors the paired AND in the RPCs).
// `property` is { first_year, retired_year, reactivated_year } (or null → treated as always-ours + ongoing).
// ⛔ A parcel carries its OWN first_year: 18 East has been ours since 2021 while its parcels come and go as
// tenants move, so a parcel created in 2024 must not inherit the building's 2021 start.
export const representedInYear = (parcel, property, year) =>
  isRepresented(parcel?.first_year, parcel?.retired_year, parcel?.reactivated_year, year) &&
  isRepresented(property?.first_year, property?.retired_year, property?.reactivated_year, year);

// Retired AS OF a viewing year: retired_year set, at/after it, and before any reactivation. Drives the virtual
// 'Retired' dropdown option (retirement is stored ONLY in retired_year — never in the per-year parcel_status column).
// ⛔ The `>=` here is now the EXACT logical complement of isRepresented's retirement clause — negate
//   (y < retired ∨ (react ≠ null ∧ y ≥ react)) and you get this line verbatim. Under the old inclusive-end
//   reading the two disagreed for exactly one year (retired_year itself showed 'Retired' in the dropdown
//   while still counting as represented everywhere else), which is what made the stamping read a year early.
//   Don't "fix" the >= — it is correct, and it is only correct because the end clause is now exclusive.
export const retiredAsOf = (retired, reactivated, year) => {
  if (retired == null) return false;
  const y = String(year);
  return y >= String(retired) && (reactivated == null || y < String(reactivated));
};

// Human badge for the effective retirement state (parcel's own, else the cascaded property's), or '' if fully ongoing.
export const retirementLabel = (parcel, property) => {
  const src = (parcel?.retired_year != null) ? parcel
            : (property?.retired_year != null) ? property : null;
  if (!src) return '';
  const who = src === property ? 'Property retired' : 'Retired';
  const r = src.retired_year, a = src.reactivated_year;
  if (a != null) {
    // The gap now STARTS AT r (r is the first year out), not r+1 as under the inclusive-end reading.
    // a > r always via the UI: ParcelModal only writes reactivated_year from a year where retiredAsOf
    // is true and valueYear !== retired_year, i.e. strictly after r.
    const ge = String(Number(a) - 1);                               // last NOT-represented year
    return String(r) === ge ? `${who} ${r}, active ${a}+` : `${who} ${r}–${ge}, active ${a}+`;
  }
  return `${who} from ${r}`;   // r is the first year OUT — "after r" would claim r was still ours
};

// ═══ WHY a year is not represented ═══
//
// ⛔ "RETIRED" MUST NOT MEAN "NOT OURS". There are two ways a year falls outside our
// representation and they are opposites: we had not taken the parcel on YET, or we had it
// and stopped. The Value Year tabs tagged every non-represented year '·retired', so a
// parcel whose first_year is 2025 read "2021 ·retired … 2024 ·retired" — asserting we
// retired something we had never held. The banner already distinguished the two; the tabs
// did not, so the two controls on the same screen disagreed about the same year.
//
// Returns null when the year IS represented, else 'not_yet' | 'retired'. One source, so a
// tab and a banner cannot diverge again.
export const notRepresentedReason = (parcel, property, year) => {
  if (representedInYear(parcel, property, year)) return null;
  const y = String(year);
  const before = (f) => f != null && f !== '' && y < String(f);
  return (before(parcel?.first_year) || before(property?.first_year)) ? 'not_yet' : 'retired';
};

// Short tag for a year chip, and the long form for its tooltip. Kept beside the reason so
// wording changes in one place.
export const NOT_REP_TAG = { not_yet: '·not yet', retired: '·retired' };
export const NOT_REP_TITLE = {
  not_yet: 'Not represented — we had not taken this on yet',
  retired: 'Not represented — retired',
};

// ═══ MAY A YEAR HOLD DATA? — a different question from "were we engaged?" ═══
//
// ⛔ THREE PREDICATES, THREE JOBS, AND THEY MUST NOT BE SWAPPED:
//
//   representedInYear()  "were we engaged?"  → GENERATES WORK. Gates every RPC, every worklist,
//                        every count, the TBT. Mirrors the SQL is_represented. UNCHANGED, and the
//                        only one any work-generating path may call.
//   yearState()          "may this hold data?" → 'represented' | 'baseline' | 'closed'. Gates DATA
//                        ENTRY only, in ParcelModal. Never gates work.
//   yearChip()           what the year tab says. Labelling only.
//
// ⛔ THE STRUCTURAL GUARANTEE IS THAT SQL NEVER LEARNS ABOUT 'baseline'. There is no yearState in the
// database, so no RPC can admit a baseline year — not by discipline, by absence. A baseline
// parcel_year_data row is invisible to expected_bills, the worklists, the counts and the TBT however
// carelessly this file is later edited. That is why the split is safe.
//
// ⛔ yearState RETURNS A STRING, NOT A BOOLEAN, ON PURPOSE. Two booleans with the same signature is
// the shape that produced `pt.tax_rate ?? scan.rate` — indistinguishable at the call site, silently
// wrong. `if (yearState(...))` is ALWAYS truthy, so reaching for the wrong one fails on the first
// test instead of quietly allowing everything. The UI needs three states anyway (·baseline vs
// ·not yet), so the safe shape is also the required one.
//
// WHY BASELINE EXISTS: taking on a client, we load the prior year or two of values and bills as the
// figures BEFORE we were involved, so savings have something to measure against. Those years are not
// ours and must generate nothing — but they must be enterable, or the baseline cannot be recorded.

// ⛔ A TYPO GUARD, NOT A RULE. Nothing about tax says three years. It stops someone who sets First
// Year to 2026 and loads a 2016 bill from silently opening a decade of empty years. Hertz may bring a
// parcel wanting more history — CHANGE THIS NUMBER, not the arithmetic below.
export const YEAR_BASELINE_SPAN = 3;

// Representation starts at the LATER of the parcel's and the property's first_year — a parcel cannot
// be ours before its building is. Null on either side means "always ours" from that side.
const representationStart = (parcel, property) => {
  const a = parcel?.first_year, b = property?.first_year;
  const av = (a == null || a === '') ? null : String(a);
  const bv = (b == null || b === '') ? null : String(b);
  if (av == null) return bv;
  if (bv == null) return av;
  return av > bv ? av : bv;
};

// ⛔ BASELINE AND A RETIREMENT GAP CANNOT OVERLAP, BY CONSTRUCTION. Baseline is only ever
// year < start; a gap is only ever year >= retired_year, and retired_year > first_year always. So
// "pre-engagement enterable, retirement gaps blocked" does not depend on the ORDER of these checks
// and cannot be broken by reordering them. Do not "simplify" the two branches together — a gap is a
// year we HAD and gave up, and writing into one produced the eight stray rows these gates exist for.
export const yearState = (parcel, property, year) => {
  if (representedInYear(parcel, property, year)) return 'represented';
  const start = representationStart(parcel, property);
  if (start == null) return 'closed';                       // no start → never a baseline year
  const y = String(year);
  if (y >= start) return 'closed';                          // at/after start but not represented = a gap
  return (Number(y) >= Number(start) - YEAR_BASELINE_SPAN) ? 'baseline' : 'closed';
};

// ⛔ ONE NAMED PREDICATE FOR THE ONE QUESTION A CALLER OUTSIDE ParcelModal ASKS. The scanner needs
// "is this bill's year a baseline year?" and nothing else; giving it yearState() would put a
// three-way string compare at a call site that cares about one branch, and `=== 'baseline'` spelled
// out at each site is how the third site gets it wrong. Still NOT a work-generating predicate —
// representedInYear remains the only one of those.
export const isBaselineYear = (parcel, property, year) =>
  yearState(parcel, property, year) === 'baseline';

// What the year tab shows. null tag = represented, nothing to say.
export const yearChip = (parcel, property, year) => {
  const state = yearState(parcel, property, year);
  if (state === 'represented') return { state, tag: null, title: '' };
  if (state === 'baseline') {
    const start = representationStart(parcel, property);
    return { state, tag: '·baseline', title:
      `Baseline year — before we were engaged (representation starts ${start}). Enterable for comparison; generates no deadlines, worklist rows or bills.` };
  }
  const why = notRepresentedReason(parcel, property, year);
  return { state, tag: NOT_REP_TAG[why], title: NOT_REP_TITLE[why] };
};
